import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UploadGuidelines = () => {
  const [activeTab, setActiveTab] = useState('requirements');

  const tabs = [
    { id: 'requirements', label: 'Requirements', icon: 'CheckSquare' },
    { id: 'examples', label: 'Examples', icon: 'FileText' },
    { id: 'tips', label: 'Tips', icon: 'Lightbulb' }
  ];

  const sampleColumns = [
    { name: 'Product Name', example: 'Samsung Galaxy S24 Ultra', required: true },
    { name: 'Brand', example: 'Samsung', required: false },
    { name: 'Model/MPN', example: 'SM-S928B', required: false },
    { name: 'EAN/UPC', example: '8806095048901', required: false },
    { name: 'SKU', example: 'SAM-S24U-256-BLK', required: false },
    { name: 'Price', example: '1199.99', required: true },
    { name: 'Currency', example: 'USD', required: false },
    { name: 'Stock/Quantity', example: '25', required: false },
    { name: 'Supplier', example: 'TechDistributor Inc', required: false }
  ];

  const requirements = [
    {
      title: 'File Format',
      items: [
        'CSV files with comma separation',
        'Excel files (.xlsx, .xls)',
        'UTF-8 encoding for international characters',
        'Maximum file size: 50MB'
      ]
    },
    {
      title: 'Required Columns',
      items: [
        'Product Name (mandatory)',
        'Price (mandatory)',
        'At least one identifier (SKU, EAN, MPN)',
        'Column headers in first row'
      ]
    },
    {
      title: 'Data Quality',
      items: [
        'No empty rows between data',
        'Consistent number format (1199.99)',
        'Valid currency codes if specified',
        'Clean product names without extra spaces'
      ]
    }
  ];

  const tips = [
    {
      icon: 'Target',
      title: 'Better Matching',
      description: 'Include EAN/UPC codes for exact product matching across suppliers'
    },
    {
      icon: 'Zap',
      title: 'Faster Processing',
      description: 'Remove unnecessary columns and empty rows before uploading'
    },
    {
      icon: 'Shield',
      title: 'Data Accuracy',
      description: 'Ensure price formats are consistent (use dots for decimals)'
    },
    {
      icon: 'Users',
      title: 'Supplier Info',
      description: 'Add supplier name column to track price sources easily'
    }
  ];

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Tab Navigation */}
      <div className="flex border-b border-border">
        {tabs?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`
              flex items-center space-x-2 px-4 py-3 text-sm font-medium transition-colors duration-150
              ${activeTab === tab?.id 
                ? 'bg-primary text-primary-foreground border-b-2 border-primary' 
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }
            `}
          >
            <Icon name={tab?.icon} size={16} />
            <span>{tab?.label}</span>
          </button>
        ))}
      </div>
      {/* Tab Content */}
      <div className="p-6">
        {activeTab === 'requirements' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">
                File Requirements
              </h3>
              <div className="grid gap-4 md:grid-cols-3">
                {requirements?.map((section, index) => (
                  <div key={index} className="space-y-3">
                    <h4 className="text-sm font-medium text-foreground">
                      {section?.title}
                    </h4>
                    <ul className="space-y-2">
                      {section?.items?.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start space-x-2 text-xs text-muted-foreground">
                          <Icon name="Check" size={12} className="text-success mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'examples' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Sample Column Structure
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Your files should include these columns for optimal matching results:
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 px-3 font-medium text-foreground">Column Name</th>
                    <th className="text-left py-2 px-3 font-medium text-foreground">Example Value</th>
                    <th className="text-left py-2 px-3 font-medium text-foreground">Required</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {sampleColumns?.map((column, index) => (
                    <tr key={index} className="hover:bg-muted">
                      <td className="py-2 px-3 font-medium text-foreground">
                        {column?.name}
                      </td>
                      <td className="py-2 px-3 text-muted-foreground font-mono text-xs">
                        {column?.example}
                      </td>
                      <td className="py-2 px-3">
                        {column?.required ? (
                          <span className="inline-flex items-center space-x-1 text-xs text-error">
                            <Icon name="AlertCircle" size={12} />
                            <span>Required</span>
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">Optional</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 p-3 bg-muted rounded-lg">
              <p className="text-xs text-muted-foreground">
                <Icon name="Info" size={12} className="inline mr-1" />
                Column names are flexible - our system will auto-detect common variations like "Product", "Item Name", "Description" for product names.
              </p>
            </div>
          </div>
        )}

        {activeTab === 'tips' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Optimization Tips
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                Follow these best practices to improve matching accuracy and processing speed:
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {tips?.map((tip, index) => (
                <div key={index} className="flex space-x-3 p-4 bg-muted rounded-lg">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                      <Icon name={tip?.icon} size={16} color="white" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-foreground mb-1">
                      {tip?.title}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      {tip?.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-primary bg-opacity-10 border border-primary border-opacity-20 rounded-lg">
              <div className="flex items-start space-x-3">
                <Icon name="Download" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-foreground mb-1">
                    Download Sample Template
                  </h4>
                  <p className="text-xs text-muted-foreground mb-3">
                    Use our pre-formatted template to ensure your data is structured correctly.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Download"
                    iconPosition="left"
                    className="text-xs"
                  >
                    Download CSV Template
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadGuidelines;